import { EyeIcon, TickIcon } from "@vericus/cadmus-icons";
import { colors, LinkButton, Text } from "@vericus/cadmus-ui";

import { AssessmentFragment, WorkSettingsFragment } from "@/generated/graphql";
import { useRootNavigate } from "@/routes";
import { formatDateFull } from "@/utils/datetime";

import { useWorkDueDates } from "../hooks/use-work-due-dates";
import { ExtensionPill } from "./extension-pill";
import * as styles from "./instructions-rail-card.css";

/**
 * Per-work "has the student opened this work's instructions yet" state,
 * mirroring the schema's own nullability rather than collapsing it away.
 * A `null` for any work means only "not opened yet" (the backend returns
 * `null` before a first open) or a degraded facts payload — never "no such
 * work". Which works exist is decided by settings, not by these values, so a
 * `null` renders its row unopened rather than hiding it. Every caller, the
 * real page and Storybook alike, supplies this directly: the component
 * fetches nothing on its own.
 */
export interface InstructionsRailCardViewed {
  group: boolean | null;
  individual: boolean | null;
  peerEvaluation: boolean | null;
}

export interface InstructionsRailCardProps {
  assessment: AssessmentFragment;
  groupWorkSettings?: WorkSettingsFragment | null;
  individualWorkSettings?: WorkSettingsFragment | null;
  /**
   * `undefined` means the payload itself is unavailable (loading or a hard
   * error), as distinct from a present payload whose per-work fields may
   * still be `null`. Rows still render in that case, since which works
   * exist is known from `assessment`'s settings regardless, but with no
   * counter: a count would claim knowledge this component doesn't have.
   */
  viewed: InstructionsRailCardViewed | undefined;
}

interface Work {
  key: keyof InstructionsRailCardViewed;
  title: string;
  dueDate: string | null;
  /**
   * Pre-extension due date, for the extension pill's tooltip. `null` for
   * Peer Evaluation (never extended) or when the work has no due date set.
   */
  originalDueDate: string | null;
  isExtended: boolean;
  onView: () => void;
}

/**
 * The rail's Instructions card, rebuilt to the prototype's own layout rather
 * than reshaping `InstructionCard`, which `Preparation` (the legacy,
 * flag-off page) still renders unchanged. This component is Home-only:
 * `preparation.tsx` imports none of it.
 */
export function InstructionsRailCard(props: InstructionsRailCardProps) {
  const { assessment, groupWorkSettings, individualWorkSettings, viewed } =
    props;
  const rootNavigate = useRootNavigate();
  const dueDates = useWorkDueDates(
    assessment,
    groupWorkSettings,
    individualWorkSettings
  );

  const candidateWorks: Work[] = [
    {
      key: "group",
      title: `Group Work: ${assessment.name}`,
      ...dueDates.group,
      onView: () => rootNavigate("/instructions/group"),
    },
  ];

  if (assessment.settings?.individualReportEnabled === true) {
    candidateWorks.push({
      key: "individual",
      title: "Individual Work",
      ...dueDates.individual,
      onView: () => rootNavigate("/instructions/individual"),
    });
  }

  if (assessment.settings?.peerEvaluationEnabled === true) {
    candidateWorks.push({
      key: "peerEvaluation",
      title: "Peer Evaluation",
      ...dueDates.peer,
      onView: () => rootNavigate("/instructions/peer"),
    });
  }

  // Which works exist is settled by settings above, not by the payload:
  // `individualReportEnabled` already gates the Individual Work row in, so a
  // `null` viewed value for it can only mean "not opened yet" or a degraded
  // facts payload — never "no such work". Every candidate therefore renders
  // (unopened when its value isn't `true`, per openedCount below); dropping a
  // row on `null` would let a transient writing-facts failure silently delete
  // a real work the student has.
  const works = candidateWorks;

  if (works.length === 0) {
    // Unreachable today: `group` is always a candidate, so `works` is never
    // empty. Left as a defensive guard in case that stops holding.
    return null;
  }

  // Fixed copy, not machine-pluralised: `works.length` is 1, 2 or 3 here,
  // never 0 (guarded above) and never more than 3 (group, individual, peer
  // are the only candidates), so the prototype's three exact strings cover
  // every case.
  const eyebrow =
    works.length === 1
      ? "Your work"
      : works.length === 2
        ? "Your two works"
        : "Your three works";
  const openedCount = viewed
    ? works.filter((work) => viewed[work.key] === true).length
    : 0;

  return (
    <div className={styles.card} data-testid="instructions-rail-card">
      <div className={styles.header}>
        <Text kind="label" as="h2" color="indigo500" bold>
          {eyebrow}
        </Text>
        {viewed && (
          <Text
            kind="bodySm"
            as="span"
            color="grey500"
            className={styles.counter}
          >
            {openedCount} of {works.length} opened
          </Text>
        )}
      </div>
      {works.map((work) => (
        <InstructionRow
          key={work.key}
          work={work}
          viewed={viewed ? viewed[work.key] === true : false}
        />
      ))}
    </div>
  );
}

function InstructionRow({ work, viewed }: { work: Work; viewed: boolean }) {
  return (
    <div
      className={styles.row}
      data-testid="instruction-row"
      data-viewed={viewed}
    >
      <Text kind="bodyLine" as="div" bold>
        {work.title}
      </Text>
      {work.dueDate !== null && (
        <div className={styles.rowDue}>
          <Text kind="bodySm" as="span">
            <strong>Due:</strong> {formatDateFull(new Date(work.dueDate))}
          </Text>
          {work.isExtended && work.originalDueDate !== null && (
            <ExtensionPill
              originalDueDate={work.originalDueDate}
              extendedDueDate={work.dueDate}
              alert
            />
          )}
        </div>
      )}
      <Text kind="bodySm" as="span" bold>
        <LinkButton
          inline
          color={viewed ? "grey500" : "indigo500"}
          icon={
            viewed ? (
              // Green tick beside a grey label is two tones, which
              // `LinkButton`'s single `color` cannot express. The colour
              // rides on the icon node rather than overriding the button.
              <TickIcon label="" size={16} color={colors.green800} />
            ) : (
              <EyeIcon label="" size={16} />
            )
          }
          onClick={work.onView}
          // All three rows otherwise share the same visible text, so
          // screen-reader users can't tell group/individual/peer apart.
          // The work title is folded into the accessible name, not just
          // exposed elsewhere, keeping the visible text inside it per
          // WCAG label-in-name.
          aria-label={
            viewed
              ? `Instructions opened for ${work.title}`
              : `Open the instructions for ${work.title}`
          }
        >
          {viewed ? "Instructions opened" : "Open the instructions"}
        </LinkButton>
      </Text>
    </div>
  );
}
